import { Immutable } from "immer";
import cookies from "js-cookie";
import React from "react";
import { filteredStoreSelector } from "tsi-common-react/src/apps/retail/selectors";
import { RetailStore } from "tsi-common-react/src/models/location";
import { isoMiles } from "tsi-common-react/src/models/nominals";
import { useAppSelector } from "tsi-common-react/src/store/reducers";
import {
    CSRF_HEADER,
    fetchJson,
    getCSRFToken,
} from "tsi-common-react/src/utils/ajax";
import { metersToMiles } from "tsi-common-react/src/utils/maps";

import { SurveyModalEmailResults } from "../SurveyModal_EmailResults";
import { SurveyModalRateWebsite } from "../SurveyModal_RateWebsite";
import { SurveyModalThanks } from "../SurveyModal_Thanks";

enum ViewState {
    INITIAL,
    EMAIL_RESULTS,
    RATE_WEBSITE,
    THANKS,
    DONE,
}

enum CookieName {
    EMAIL_RESULTS = "locator_survey_email_taken",
    RATE_WEBSITE = "locator_survey_rating_taken",
    DISMISSED = "locator_survey_dismissed",
}

enum CookieValue {
    YES = "Y",
    NO = "N",
}

const SUBMIT_AUTH_KEY = "c78q87n0x44f";
const SUBMIT_BRAND = "SY";
const SURVEY_PREPOP_MODE_PAUSE = 50 * 1000; // seconds to pause in pre-pop mode

type EnumType = typeof CookieName;
type EnumKeyType = keyof EnumType;

export const getStoresForEmail = (stores: Immutable<RetailStore[]>) => {
    return stores.slice(0, 3).map((s) => ({
        id: s.external_id,
        distance:
            s.distance == null
                ? null
                : Number(isoMiles.unwrap(metersToMiles(s.distance)).toFixed(2)),
    }));
};

const resetCookies = () => {
    Object.keys(CookieName).map((key) => {
        cookies.set(CookieName[key as EnumKeyType], CookieValue.NO);
    });
};

const determineInitialViewState = (
    currentView: ViewState,
    storesCount: number,
): ViewState => {
    if (currentView !== ViewState.INITIAL) {
        return currentView;
    }
    if (storesCount <= 0) {
        return currentView;
    }
    // Was closed?
    if (cookies.get(CookieName.DISMISSED) === CookieValue.YES) {
        return ViewState.DONE;
    }
    // Need to email results?
    if (cookies.get(CookieName.EMAIL_RESULTS) !== CookieValue.YES) {
        return ViewState.EMAIL_RESULTS;
    }
    // Need to rate website?
    if (cookies.get(CookieName.RATE_WEBSITE) !== CookieValue.YES) {
        return ViewState.RATE_WEBSITE;
    }
    // Must be done
    return ViewState.DONE;
};

export const SurveyModal = (): React.ReactNode => {
    const stores = useAppSelector(filteredStoreSelector);
    const storesRef = React.useRef(stores);
    const [view, setView] = React.useState<ViewState>(ViewState.INITIAL);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [emailAddr, setEmailAddr] = React.useState("");
    const initialStoresListRef = React.useRef(stores);
    const isInitializedRef = React.useRef(false);

    const onClose = () => {
        cookies.set(CookieName.DISMISSED, CookieValue.YES);
        setView(ViewState.DONE);
    };

    const onSubmitEmail = async ({ recipient }: { recipient: string }) => {
        setIsSubmitting(true);
        setEmailAddr(recipient);

        const data = {
            email: recipient,
            stores: getStoresForEmail(stores),
        };
        try {
            await fetchJson("/api/email-retail-locator-results", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    [CSRF_HEADER]: await getCSRFToken(),
                },
                body: JSON.stringify(data),
            });
        } catch (e) {
            console.error(e);
        }
        setIsSubmitting(false);
        cookies.set(CookieName.EMAIL_RESULTS, CookieValue.YES);
        setView(ViewState.RATE_WEBSITE);
    };

    const onSubmitRating = async ({ rating }: { rating: number | null }) => {
        setIsSubmitting(true);

        const data = {
            email: emailAddr,
            auth: SUBMIT_AUTH_KEY,
            brand: SUBMIT_BRAND,
            survey_modal_result: String(rating),
        };
        try {
            await fetch(
                "https://api.promosissweeps.com/rest/tsi-2190/survey.php",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                    },
                    body: new URLSearchParams(data).toString(),
                },
            );
        } catch (e) {
            console.log(e);
        }
        setIsSubmitting(false);
        cookies.set(CookieName.RATE_WEBSITE, CookieValue.YES);
        setView(ViewState.THANKS);

        setTimeout(() => {
            onClose();
        }, 5_000);
    };

    // Keep a live reference to the latest stores for deferred initialization
    React.useEffect(() => {
        storesRef.current = stores;
    }, [stores]);

    // Initial mount effect - delay initialization once on mount
    React.useEffect(() => {
        const timeoutId = setTimeout(() => {
            isInitializedRef.current = true;
            const newView = determineInitialViewState(
                ViewState.INITIAL,
                storesRef.current.length,
            );
            if (newView !== ViewState.INITIAL) {
                setView(newView);
            }
        }, SURVEY_PREPOP_MODE_PAUSE);

        return () => clearTimeout(timeoutId);
    }, []);

    // Update effect - handle new search results and view state changes
    React.useEffect(() => {
        if (!isInitializedRef.current) {
            return;
        }

        const hasNewResults = initialStoresListRef.current !== stores;
        if (hasNewResults && view === ViewState.DONE) {
            // Reset to initial state when new search results come in
            initialStoresListRef.current = stores;
            setView(ViewState.INITIAL);
            resetCookies();
        } else {
            const newView = determineInitialViewState(view, stores.length);
            if (newView !== view) {
                setView(newView);
            }
        }
    }, [stores, view]);

    return (
        <>
            <SurveyModalEmailResults
                isOpen={view === ViewState.EMAIL_RESULTS}
                isSubmitting={isSubmitting}
                onClose={onClose}
                onSubmit={onSubmitEmail}
            />
            <SurveyModalRateWebsite
                isOpen={view === ViewState.RATE_WEBSITE}
                isSubmitting={isSubmitting}
                onClose={onClose}
                onSubmit={onSubmitRating}
            />
            <SurveyModalThanks
                isOpen={view === ViewState.THANKS}
                onClose={onClose}
            />
        </>
    );
};
